import type { IView } from '@barba/core';
import { IdleQueue } from 'idlize/IdleQueue.mjs';
import certificatesSlider from '../inits/pages/catalog/certificates-slider';
import pageAnimations from '../animations/pages/catalog/init-page-animations';
import type { ContentList as ContentListType } from '@chipsadesign/content-list';
import { fetchContentList, fetchContentListFilter } from '../dynamic-modules';
import reveal from '../inits/reveal';
import headerTheme from '@/inits/components/header/header-theme';
import gsap from 'gsap';
import headerCartHandlers from '@/inits/components/header/header-cart-handlers';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { lazyload } from '@/inits/lazy-load';
import { openDynamicBanner } from '@/components/dynamic-banner';
import { getErrorMessageByLang } from '@/utils/getErrorMessage';

const beforeEnterQueue = new IdleQueue();
const header = document.querySelector('.js-header');
let catalogEl: HTMLElement | null;
let filterContainer: HTMLElement | null;
let catalog: ContentListType | null;
const htmlElement = document.querySelector('html');
const langValue = htmlElement?.getAttribute('lang');
let errorMessage = '';
if (langValue) {
    errorMessage = getErrorMessageByLang(langValue);
}

export default {
    namespace: 'catalog-page',

    beforeEnter({ next }) {
        beforeEnterQueue.pushTask(() => {
            pageAnimations.init();

            catalogEl = next.container.querySelector<HTMLElement>('.js-catalog');
            filterContainer = next.container.querySelector<HTMLElement>('.js-catalog-filters');
            const loaderContainer = next.container.querySelector('.js-n-list-loader-container');

            if (catalogEl) {
                Promise.all([fetchContentList(), fetchContentListFilter()]).then(([ContentList, ContentListFilter]) => {
                    if (catalogEl) {
                        catalog = new ContentList(catalogEl, {
                            modules: [ContentListFilter],
                            pushState: false,
                            cacheRequests: true,
                            scrollTo: {
                                el: filterContainer || catalogEl,
                                offset: (header ? -header.getBoundingClientRect().height : 0) - 30,
                            },
                            onFetchStart: () => {
                                const filterItems =
                                    document.querySelectorAll<HTMLInputElement>('.js-catalog-filters-item');

                                filterItems.forEach((item) => (item.disabled = true));

                                gsap.to('.js-catalog', {
                                    opacity: 0,
                                    duration: 0.5,
                                    ease: 'ease',
                                });

                                loaderContainer?.classList.add('visible');
                            },
                            onFetchSuccess: async (instance, response) => {
                                headerCartHandlers.destroyForPage();

                                const transitionTimeline = gsap.timeline({ duration: 0.5, ease: 'ease' });

                                transitionTimeline.to('.js-catalog', {
                                    opacity: 0,
                                    onComplete: () => {
                                        if (response.data) {
                                            if (response.data.contentHtml && instance.contentEl) {
                                                instance.contentEl.innerHTML = response.data.contentHtml;
                                                lazyload.update();

                                                requestAnimationFrame(() => {
                                                    if (instance.contentEl) {
                                                        reveal.init(instance.contentEl);
                                                    }
                                                });
                                            }

                                            if (response.data.nextUrl) {
                                                window.history.pushState(null, '', response.data.nextUrl);
                                            }

                                            ScrollTrigger.refresh();
                                            headerTheme.destroy();
                                            headerTheme.init();

                                            headerCartHandlers.initForPage();
                                        }
                                    },
                                });

                                loaderContainer?.classList.remove('visible');

                                transitionTimeline.to('.js-catalog', { opacity: 1 });

                                const filterItems =
                                    document.querySelectorAll<HTMLInputElement>('.js-catalog-filters-item');

                                filterItems.forEach((item) => (item.disabled = false));
                            },
                            onFetchAbort: () => {
                                const filterItems =
                                    document.querySelectorAll<HTMLInputElement>('.js-catalog-filters-item');

                                filterItems.forEach((item) => (item.disabled = false));

                                loaderContainer?.classList.remove('visible');

                                gsap.to('.js-news', {
                                    opacity: 1,
                                    duration: 0.5,
                                    ease: 'ease',
                                });
                            },
                            onFetchError: () => {
                                const filterItems =
                                    document.querySelectorAll<HTMLInputElement>('.js-catalog-filters-item');

                                filterItems.forEach((item) => (item.disabled = false));

                                loaderContainer?.classList.remove('visible');

                                gsap.to('.js-news', {
                                    opacity: 1,
                                    duration: 0.5,
                                    ease: 'ease',
                                });

                                openDynamicBanner(errorMessage, true);
                            },
                            filter: {
                                el: filterContainer,
                                filterOnInputChange: true,
                            },
                        });
                    }
                });
            }
        });

        beforeEnterQueue.pushTask(() => {
            certificatesSlider.init();

            headerCartHandlers.initForPage();
        });
    },

    beforeLeave() {
        beforeEnterQueue.clearPendingTasks();

        if (catalog) {
            catalog.destroy();
            catalog = null;
        }

        certificatesSlider.destroy();
        headerCartHandlers.destroyForPage();
    },
} as IView;
